import * as React from "react";

import { Column } from "@tanstack/react-table";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";

import { CheckIcon } from "@radix-ui/react-icons";
import { Button } from "../ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "../ui/command";

import { Badge } from "../ui/badge";
import { cn } from "@/lib/utils/ui";
import { ChevronDown, X } from "lucide-react";
import { Checkbox } from "../ui/checkbox";
import {
  Avatar,
  AvatarFallback,
  AvatarGroup,
  AvatarGroupCount,
  AvatarImage,
} from "../ui/avatar";

function FilterOptionAvatar({
  option,
  className,
}: {
  option: DataTableFilterOptions;
  className?: string;
}) {
  if (!option.avatarFallback && !option.avatarUrl && !option.leading) {
    return null;
  }
  if (!option.avatarFallback && !option.avatarUrl) {
    return option.leading;
  }
  return (
    <Avatar className={cn("h-6 w-6 border border-background", className)}>
      <AvatarImage src={option.avatarUrl ?? undefined} alt={option.label} />
      <AvatarFallback className="text-[10px]">
        {option.avatarFallback ?? option.label[0] ?? "?"}
      </AvatarFallback>
    </Avatar>
  );
}

export interface DataTableFilterOptions {
  label: string;
  value: string;
  icon?: React.ComponentType<{ className?: string }>;
  color?: string | null;
  leading?: React.ReactNode;
  avatarUrl?: string | null;
  avatarFallback?: string;
}

interface DataTableSelectFilterProps<TData, TValue> {
  column?: Column<TData, TValue>;
  title?: string;
  options: DataTableFilterOptions[];
  allLabel?: string;
  stackSelected?: boolean;
}

interface DataTableSingleSelectFilterProps<TData, TValue> {
  column?: Column<TData, TValue>;
  title?: string;
  options: DataTableFilterOptions[];
  includeAllOption?: boolean;
  allLabel?: string;
}

export function DataTableSelectFilter<TData, TValue>({
  column,
  title,
  options,
  allLabel,
  stackSelected = false,
}: DataTableSelectFilterProps<TData, TValue>) {
  const [open, setOpen] = React.useState(false);
  const selectedValues = new Set(column?.getFilterValue() as string[]);
  const updateSelectedValues = (nextValues: Set<string>) => {
    const filterValues = Array.from(nextValues);
    column?.setFilterValue(filterValues.length ? filterValues : undefined);
  };
  const selectedOptions = options.filter((option) =>
    selectedValues.has(option.value),
  );
  const compactTrigger = Boolean(allLabel);
  const emptyLabel = allLabel ?? (title ? `Select ${title}` : "Select");

  let triggerLabel: React.ReactNode;
  if (selectedValues.size === 0) {
    triggerLabel = (
      <span className={compactTrigger ? undefined : "w-full text-start opacity-50"}>
        {emptyLabel}
      </span>
    );
  } else if (stackSelected && selectedValues.size > 0) {
    const visible = selectedOptions.slice(0, 3);
    const remaining = selectedOptions.length - visible.length;
    triggerLabel = (
      <span className="flex min-w-0 items-center gap-2">
        {title ? <span>{title}</span> : null}
        <AvatarGroup className="items-center">
          {visible.map((option) => (
            <FilterOptionAvatar key={option.value} option={option} />
          ))}
          {remaining > 0 ? (
            <AvatarGroupCount className="size-6 text-[10px]">
              +{remaining}
            </AvatarGroupCount>
          ) : null}
        </AvatarGroup>
      </span>
    );
  } else {
    triggerLabel = (
      <div className="flex w-full flex-wrap gap-1">
        {selectedOptions.map((option) => (
          <Badge
            variant="secondary"
            key={option.value}
            className="gap-1 rounded-sm px-1 font-normal hover:cursor-default"
          >
            {option.color && (
              <span
                className="h-2 w-2 shrink-0 rounded-full"
                style={{ backgroundColor: option.color }}
              />
            )}
            {option.leading}
            {option.label}
            <span
              className="hover:cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                const newSelectedValues = new Set(selectedValues);
                newSelectedValues.delete(option.value);
                updateSelectedValues(newSelectedValues);
              }}
            >
              <X className="ml-1 size-0.5 opacity-50" />
            </span>
          </Badge>
        ))}
      </div>
    );
  }

  return (
    <Popover modal={true} open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant={"outline"}
          className="h-fit min-w-40 justify-between hover:border-primary hover:bg-inherit"
        >
          {triggerLabel}
          <ChevronDown />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0" align="start">
        <Command>
          <CommandInput placeholder={title} />
          <CommandList>
            <CommandEmpty>No results found.</CommandEmpty>
            <CommandGroup>
              {options.map((option) => {
                const isSelected = selectedValues.has(option.value);
                return (
                  <CommandItem
                    key={option.value}
                    onSelect={() => {
                      const newSelectedValues = new Set(selectedValues);
                      if (isSelected) {
                        newSelectedValues.delete(option.value);
                      } else {
                        newSelectedValues.add(option.value);
                      }
                      updateSelectedValues(newSelectedValues);
                    }}
                    className="gap-3"
                  >
                    <span
                      onClick={(event) => event.stopPropagation()}
                      onPointerDown={(event) => event.stopPropagation()}
                    >
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => {
                          const newSelectedValues = new Set(selectedValues);
                          if (isSelected) {
                            newSelectedValues.delete(option.value);
                          } else {
                            newSelectedValues.add(option.value);
                          }
                          updateSelectedValues(newSelectedValues);
                        }}
                      />
                    </span>
                    {option.color && (
                      <span
                        className="h-3 w-3 shrink-0 rounded-full"
                        style={{ backgroundColor: option.color }}
                      />
                    )}
                    <FilterOptionAvatar option={option} />
                    {option.icon && (
                      <option.icon className="h-4 w-4 text-muted-foreground" />
                    )}
                    <span>{option.label}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
            {selectedValues.size > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    onSelect={() => {
                      column?.setFilterValue(undefined);
                      setOpen(false);
                    }}
                    className="justify-center text-center"
                  >
                    Clear filters
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export function DataTableSingleSelectFilter<TData, TValue>({
  column,
  title,
  options,
  includeAllOption = true,
  allLabel,
}: DataTableSingleSelectFilterProps<TData, TValue>) {
  const [open, setOpen] = React.useState(false);
  const selectedValue = column?.getFilterValue() as string | undefined;
  const selectedOption = options.find((option) => option.value === selectedValue);
  const buttonLabel = selectedOption
    ? allLabel
      ? selectedOption.label
      : `${title}: ${selectedOption.label}`
    : (allLabel ?? `${title}: All`);
  const hasIconColumn = options.some((option) => option.icon || option.leading);
  const itemClassName = "flex items-center gap-2";

  return (
    <Popover modal={true} open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant={"outline"}
          className="min-w-40 h-fit hover:bg-inherit hover:border-primary justify-between"
        >
          <span className="flex items-center gap-2">
            {selectedOption?.icon && (
              <selectedOption.icon className="h-4 w-4 text-muted-foreground" />
            )}
            {selectedOption?.leading}
            {buttonLabel}
          </span>
          <ChevronDown />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-full" align="start">
        <Command>
          <CommandList>
            <CommandGroup>
              {includeAllOption && (
                <CommandItem
                  value="all"
                  onSelect={() => {
                    column?.setFilterValue(undefined);
                    setOpen(false);
                  }}
                  className={itemClassName}
                >
                  <span className="min-w-0 flex-1">{allLabel ?? "All"}</span>
                  <CheckIcon
                    className={cn(
                      "size-4 shrink-0",
                      !selectedValue ? "opacity-100" : "opacity-0",
                    )}
                  />
                </CommandItem>
              )}
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.value}
                  onSelect={() => {
                    column?.setFilterValue(option.value);
                    setOpen(false);
                  }}
                  className={itemClassName}
                >
                  {hasIconColumn ? (
                    <span className="flex size-4 shrink-0 items-center justify-center">
                      {option.icon ? (
                        <option.icon className="size-4 text-muted-foreground" />
                      ) : (
                        option.leading
                      )}
                    </span>
                  ) : null}
                  <span className="min-w-0 flex-1">{option.label}</span>
                  <CheckIcon
                    className={cn(
                      "size-4 shrink-0",
                      option.value === selectedValue
                        ? "opacity-100"
                        : "opacity-0",
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
